/**
 * Earnings Corrections — the pure model behind the Corrections tab: money
 * formatting and the typed-amount confirmation, month ranges, status and flag
 * labels, and the selection → draft builders.
 *
 * Nothing here prices a line. Proposal targets come from the server, manual
 * lines carry exactly what the reviewer typed, and the server re-measures
 * every ChannelMeter base (and the net change the apply dialog confirms) on
 * save.
 */

import type { StatusVariant } from '../../ui';
import type {
  CorrectionDraftInput,
  CorrectionKind,
  CorrectionLine,
  CorrectionLineInput,
  CorrectionSourceMonth,
  CorrectionStatus,
  DsProposal,
  MonthRow,
  ProposalFlag,
  ProposalLine,
  ProposalMonth,
  SettlementStatus,
} from '../../../services/earningsCorrectionsApi';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** The backend's cent rounding (utils/earningsCorrections.toCents), float-noise safe. */
export const toCents = (usd: number): number => Math.round(Math.round(usd * 1e6) / 1e4);

export const formatUsd = (usd: number | null | undefined): string => {
  if (usd == null || !Number.isFinite(usd)) return '—';
  const cents = toCents(usd);
  const abs = (Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cents < 0 ? `-$${abs}` : `$${abs}`;
};

export const formatCents = (cents: number): string => formatUsd(cents / 100);

/** "+$1.00" / "-$24,118.77" / "$0.00" — how every net change is shown (and typed back). */
export const formatSignedCents = (cents: number): string => (cents > 0 ? `+${formatCents(cents)}` : formatCents(cents));

export const formatCount = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-US');

const MONEY_RE = /^([+\-−–])?\$?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/;

/**
 * Parse a typed dollar amount into integer cents. Accepts "$", thousands
 * commas, up to 2 decimals, a leading +/-/−/– sign (before or after "$") and
 * accounting parentheses for negatives. Anything else is null — never guessed.
 */
export function parseMoneyToCents(raw: string): number | null {
  let s = raw.trim().replace(/\s+/g, '');
  if (!s) return null;
  let negative = false;
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    negative = true;
    s = paren[1];
  }
  s = s.replace(/^\$([+\-−–])/, '$1$'); // "$-5" reads like "-$5"
  const m = MONEY_RE.exec(s);
  if (!m) return null;
  if (m[1]) {
    if (paren) return null; // "(-5)" / "(+5)" is ambiguous
    if (m[1] !== '+') negative = true;
  }
  const cents = Number(m[2].replace(/,/g, '')) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) return null;
  if (cents === 0) return 0;
  return negative ? -cents : cents;
}

/** The apply gate: the reviewer typed exactly the net change, sign included. */
export const typedAmountMatches = (typed: string, expectedCents: number): boolean => parseMoneyToCents(typed) === expectedCents;

/** A non-negative dollar amount (manual line values); null when not one. */
export function parseUsdInput(raw: string): number | null {
  const cents = parseMoneyToCents(raw);
  return cents == null || cents < 0 ? null : cents / 100;
}

/** A non-negative whole number with optional thousands commas; null when not one. */
export function parseCountInput(raw: string): number | null {
  const s = raw.trim().replace(/\s+/g, '');
  if (!/^(\d{1,3}(,\d{3})+|\d+)$/.test(s)) return null;
  const n = Number(s.replace(/,/g, ''));
  return Number.isSafeInteger(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export const isMonth = (s: string): boolean => MONTH_RE.test(s);

const monthIndex = (ym: string): number => {
  const [y, m] = ym.split('-').map(Number);
  return y * 12 + (m - 1);
};

const monthFromIndex = (i: number): string => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;

export const monthLabel = (ym: string): string => {
  const m = MONTH_RE.exec(ym);
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : ym;
};

/** Contiguous runs, "Jun–Aug 2026" / "Dec 2025–Feb 2026", joined with ", ". */
export function monthRangeLabel(months: readonly string[]): string {
  const sorted = Array.from(new Set(months.filter(isMonth))).sort();
  if (sorted.length === 0) return '—';
  const runs: Array<[string, string]> = [];
  for (const m of sorted) {
    const last = runs[runs.length - 1];
    if (last && monthIndex(m) === monthIndex(last[1]) + 1) last[1] = m;
    else runs.push([m, m]);
  }
  return runs
    .map(([from, to]) => {
      if (from === to) return monthLabel(from);
      const [fy] = from.split('-');
      const [ty] = to.split('-');
      return fy === ty
        ? `${MONTH_NAMES[Number(from.slice(5)) - 1]}–${monthLabel(to)}`
        : `${monthLabel(from)}–${monthLabel(to)}`;
    })
    .join(', ');
}

/** Inclusive YYYY-MM range, oldest first (empty when either end is invalid or reversed). */
export function monthsBetween(from: string, to: string): string[] {
  if (!isMonth(from) || !isMonth(to)) return [];
  const out: string[] = [];
  for (let i = monthIndex(from); i <= monthIndex(to); i++) out.push(monthFromIndex(i));
  return out;
}

/** The month before `ym` (the default "last completed month" for new corrections). */
export const previousMonth = (ym: string): string => monthFromIndex(monthIndex(ym) - 1);

export const currentMonth = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const STATUS_META: Record<CorrectionStatus, { label: string; variant: StatusVariant; description: string }> = {
  draft: { label: 'Draft', variant: 'draft', description: 'Saved but not applied — no surface shows it yet.' },
  applied: { label: 'Applied', variant: 'ok', description: 'Shown on the Earnings tab, in chat and on client report pages.' },
  reverted: { label: 'Reverted', variant: 'paused', description: 'Rolled back — every surface shows ChannelMeter figures again.' },
  superseded: { label: 'Superseded', variant: 'approved', description: 'Replaced by a later correction.' },
  discarded: { label: 'Discarded', variant: 'paused', description: 'A draft that was thrown away; never applied.' },
};

export const SETTLEMENT_META: Record<SettlementStatus, { label: string; variant: StatusVariant }> = {
  pending: { label: 'Settlement pending', variant: 'pending' },
  settled: { label: 'Settled', variant: 'ok' },
  not_required: { label: 'No settlement needed', variant: 'draft' },
};

export const KIND_LABEL: Record<CorrectionKind, string> = {
  direct_sold_reprocess: 'DS reprocess',
  manual: 'Manual',
};

export const BUCKET_LABEL = { directSold: 'Direct Sold', adSense: 'AdSense' } as const;

export type FlagTone = 'warning' | 'danger' | 'info' | 'neutral';

export const FLAG_META: Record<ProposalFlag, { label: string; tone: FlagTone; description: string }> = {
  requires_review: {
    label: 'Needs review',
    tone: 'warning',
    description: 'Never pre-selected: tick it only after checking it by hand.',
  },
  unusual: {
    label: 'Unusual',
    tone: 'danger',
    description: "Changes more than 50% of ChannelMeter's earnings, or puts Direct Sold above the channel's AdSense.",
  },
  needs_user: {
    label: 'New row',
    tone: 'warning',
    description: 'ChannelMeter has no Direct Sold row for this channel this month; the payee is a suggestion.',
  },
  ambiguous_multi_user: {
    label: 'Split across payees',
    tone: 'warning',
    description: "The channel's Direct Sold sits under 2+ ChannelMeter payees; the new amount is split by their gross.",
  },
  matched_by_name: {
    label: 'Matched by name',
    tone: 'warning',
    description: 'Paired with a ChannelMeter row by channel name because the channel ids differ.',
  },
  assumed_split: {
    label: 'Assumed 50% split',
    tone: 'warning',
    description: 'No ChannelMeter split to copy, so earnings = gross × 0.5.',
  },
  already_corrected: {
    label: 'Already corrected',
    tone: 'info',
    description: 'An applied correction owns this row; applying this one would supersede it.',
  },
  pre_existing: {
    label: 'Pre-existing',
    tone: 'neutral',
    description: "This month's pipeline output is unchanged since ChannelMeter took it, so the difference pre-dates any re-upload.",
  },
};

const SOURCE_FLAG_META: Record<string, { label: string; tone: FlagTone; description: string }> = {
  no_cm_anchor: {
    label: 'No ChannelMeter anchor',
    tone: 'warning',
    description: 'No pipeline record shows when ChannelMeter took this month.',
  },
  rate_card_changed_since_cm: {
    label: 'Rate card changed',
    tone: 'warning',
    description: 'The YT payout rate card changed after ChannelMeter took this month, so more than the upload may have moved.',
  },
  upload_replaced_since_cm: {
    label: 'Upload replaced',
    tone: 'info',
    description: 'The GAM upload was replaced after ChannelMeter took this month (expected for a reprocess).',
  },
  upload_after_cm_snapshot: {
    label: 'Upload newer than CM snapshot',
    tone: 'info',
    description: 'The current GAM upload is newer than the stored ChannelMeter snapshot.',
  },
  pipeline_changed_since_cm: {
    label: 'Pipeline changed',
    tone: 'info',
    description: "Today's recomputed Direct Sold total differs from what ChannelMeter was given.",
  },
  pipeline_unchanged_since_cm: {
    label: 'Pipeline unchanged',
    tone: 'neutral',
    description: "Today's recomputed total equals what ChannelMeter was given, so any difference pre-dates a re-upload.",
  },
  no_full_run_since_upload: {
    label: 'No full run since upload',
    tone: 'warning',
    description: 'No completed full run since the upload, so the stored ad records may be stale.',
  },
};

export const sourceFlagMeta = (flag: string): { label: string; tone: FlagTone; description: string } =>
  SOURCE_FLAG_META[flag] ?? { label: flag.replace(/_/g, ' '), tone: 'neutral', description: flag };

// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------

/** Corrected − ChannelMeter creator earnings for one saved line, in cents. */
export const lineDeltaCents = (line: Pick<CorrectionLine, 'target' | 'base'>): number =>
  toCents(line.target?.earningsUsd ?? 0) - toCents(line.base.earningsUsd);

const CHANNEL_ID_RE = /^UC[\w-]{22}$/;
/** Only real YouTube channels can be corrected (the API's rule). */
export const isCorrectableChannelId = (id: string): boolean => CHANNEL_ID_RE.test(id);

/** Exactly the keys the API's strict line schema accepts. */
export function toLineInput(input: CorrectionLineInput): CorrectionLineInput {
  return {
    month: input.month,
    bucket: input.bucket,
    userKey: input.userKey,
    channelId: input.channelId,
    channelName: input.channelName,
    revenueSource: input.revenueSource,
    op: input.op,
    target:
      input.op === 'set' && input.target
        ? {
            grossUsd: input.target.grossUsd,
            earningsUsd: input.target.earningsUsd,
            performance: input.target.performance,
            performanceMetric: input.target.performanceMetric ?? null,
          }
        : null,
    note: input.note ?? null,
  };
}

/** Exactly the keys the API's strict source schema accepts (drops the proposal's `anchor`). */
export function toSourceMonth(source: ProposalMonth['source'] | CorrectionSourceMonth): CorrectionSourceMonth {
  return {
    month: source.month,
    uploadFileName: source.uploadFileName,
    uploadedAt: source.uploadedAt,
    uploadedBy: source.uploadedBy,
    uploadContentHash: source.uploadContentHash,
    replacedPreviousAt: source.replacedPreviousAt,
    rateCardUpdatedAt: source.rateCardUpdatedAt,
    computedAt: source.computedAt,
    flags: [...source.flags],
  };
}

// ---------------------------------------------------------------------------
// Draft fields
// ---------------------------------------------------------------------------

export const CLIENT_NOTE_MAX = 140;

export interface DraftFields {
  title: string;
  reason: string;
  clientNote: string;
}

/** The backend's client-note normalization (control chars → space, whitespace collapsed); null when empty. */
export function normalizeClientNoteInput(raw: string): string | null {
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || null;
}

/** First problem with the title / reason / client note, or null. The note is never silently cut. */
export function validateDraftFields(fields: DraftFields): string | null {
  const title = fields.title.trim();
  if (title.length < 3) return 'Give the correction a title (3+ characters).';
  if (title.length > 120) return 'Keep the title to 120 characters.';
  const reason = fields.reason.trim();
  if (reason.length < 3) return 'Explain why (internal only, 3+ characters).';
  if (reason.length > 2000) return 'Keep the reason to 2,000 characters.';
  const note = normalizeClientNoteInput(fields.clientNote);
  if (note && note.length > CLIENT_NOTE_MAX) return `Keep the client note to ${CLIENT_NOTE_MAX} characters (it is ${note.length}).`;
  return null;
}

// ---------------------------------------------------------------------------
// Direct Sold proposal → draft
// ---------------------------------------------------------------------------

export const proposalLineKey = (month: string, index: number): string => `${month}#${index}`;

/** Lines the server pre-selected (and that are draftable). */
export function initialSelection(proposal: DsProposal): Set<string> {
  const keys = new Set<string>();
  for (const m of proposal.months) {
    m.lines.forEach((line, i) => {
      if (line.selected && line.input) keys.add(proposalLineKey(m.month, i));
    });
  }
  return keys;
}

const REVIEW_FLAGS: ReadonlySet<ProposalFlag> = new Set<ProposalFlag>(['requires_review', 'needs_user', 'already_corrected', 'pre_existing']);

export const needsReview = (line: Pick<ProposalLine, 'flags'>): boolean => line.flags.some((f) => REVIEW_FLAGS.has(f));

export interface SelectionSummary {
  lines: number;
  /** Selected lines the server would not have pre-selected. */
  reviewed: number;
  deltaEarningsCents: number;
  byMonth: Array<{ month: string; lines: number; deltaEarningsCents: number }>;
  /** Selected lines owned by an applied correction (applying needs to supersede it). */
  coveredBy: string[];
}

export function summarizeSelection(proposal: DsProposal, selected: ReadonlySet<string>): SelectionSummary {
  const summary: SelectionSummary = { lines: 0, reviewed: 0, deltaEarningsCents: 0, byMonth: [], coveredBy: [] };
  const covered = new Set<string>();
  for (const m of proposal.months) {
    let lines = 0;
    let delta = 0;
    m.lines.forEach((line, i) => {
      if (!line.input || !selected.has(proposalLineKey(m.month, i))) return;
      lines += 1;
      delta += toCents(line.deltaEarningsUsd);
      if (needsReview(line)) summary.reviewed += 1;
      if (line.coveredBy) covered.add(line.coveredBy);
    });
    if (lines > 0) summary.byMonth.push({ month: m.month, lines, deltaEarningsCents: delta });
    summary.lines += lines;
    summary.deltaEarningsCents += delta;
  }
  summary.coveredBy = Array.from(covered);
  return summary;
}

/**
 * The draft a proposal review saves — the backend's draftFromProposal, with
 * the reviewer's own selection. Each line notes the flags it was proposed
 * with; sources cover only the months that kept a line.
 */
export function buildProposalDraft(proposal: DsProposal, selected: ReadonlySet<string>, fields: DraftFields): CorrectionDraftInput {
  const lines: CorrectionLineInput[] = [];
  const monthsWithLines = new Set<string>();
  for (const m of proposal.months) {
    m.lines.forEach((line, i) => {
      if (!line.input || !selected.has(proposalLineKey(m.month, i))) return;
      lines.push({ ...toLineInput(line.input), note: line.flags.length ? `proposal flags: ${line.flags.join(', ')}` : null });
      monthsWithLines.add(m.month);
    });
  }
  return {
    title: fields.title.trim(),
    reason: fields.reason.trim(),
    kind: 'direct_sold_reprocess',
    lines,
    sources: proposal.months.filter((m) => monthsWithLines.has(m.month)).map((m) => toSourceMonth(m.source)),
    clientNote: normalizeClientNoteInput(fields.clientNote),
    settlement: { status: 'pending' },
  };
}

// ---------------------------------------------------------------------------
// Manual lines
// ---------------------------------------------------------------------------

export interface ManualLineDraft {
  row: MonthRow;
  op: 'set' | 'remove';
  grossUsd: string;
  earningsUsd: string;
  performance: string;
  note: string;
}

const plainUsd = (usd: number): string => (toCents(usd) / 100).toFixed(2);

/** A new manual line, pre-filled with ChannelMeter's current values. */
export const manualDraftFromRow = (row: MonthRow): ManualLineDraft => ({
  row,
  op: 'set',
  grossUsd: plainUsd(row.values.grossUsd),
  earningsUsd: plainUsd(row.values.earningsUsd),
  performance: String(Math.round(row.values.performance)),
  note: '',
});

export type ManualLineResult = { input: CorrectionLineInput; error?: undefined } | { input?: undefined; error: string };

export function parseManualLine(d: ManualLineDraft): ManualLineResult {
  const { row } = d;
  const key = {
    month: row.month,
    bucket: row.bucket,
    userKey: row.userKey,
    channelId: row.channelId,
    channelName: row.channelName,
    revenueSource: row.revenueSource,
  };
  const note = d.note.trim() ? d.note.trim().slice(0, 500) : null;
  if (!isCorrectableChannelId(row.channelId)) return { error: 'Only real YouTube channels (UC… ids) can be corrected.' };
  if (d.op === 'remove') {
    if (row.values.rowCount === 0) return { error: 'ChannelMeter has nothing to remove here.' };
    return { input: { ...key, op: 'remove', target: null, note } };
  }
  const grossUsd = parseUsdInput(d.grossUsd);
  const earningsUsd = parseUsdInput(d.earningsUsd);
  const performance = parseCountInput(d.performance);
  if (grossUsd == null) return { error: 'Gross must be a dollar amount (up to 2 decimals).' };
  if (earningsUsd == null) return { error: 'Earnings must be a dollar amount (up to 2 decimals).' };
  if (performance == null) return { error: `${row.values.performanceMetric ?? 'Performance'} must be a whole number.` };
  if (earningsUsd > grossUsd) return { error: 'Creator earnings cannot exceed gross.' };
  // The server's no-change rule (valuesDrifted with the row count held equal).
  const unchanged =
    Math.abs(grossUsd - row.values.grossUsd) <= 0.005 &&
    Math.abs(earningsUsd - row.values.earningsUsd) <= 0.005 &&
    Math.abs(performance - row.values.performance) <= 0.5;
  if (unchanged) return { error: 'These are ChannelMeter’s current values — nothing would change.' };
  return {
    input: {
      ...key,
      op: 'set',
      target: { grossUsd, earningsUsd, performance, performanceMetric: row.values.performanceMetric ?? null },
      note,
    },
  };
}

/** Corrected − ChannelMeter earnings for a manual line as typed (null while unparsable). */
export function manualDeltaCents(d: ManualLineDraft): number | null {
  if (d.op === 'remove') return -toCents(d.row.values.earningsUsd);
  const earnings = parseUsdInput(d.earningsUsd);
  return earnings == null ? null : toCents(earnings) - toCents(d.row.values.earningsUsd);
}

export function buildManualDraft(
  drafts: readonly ManualLineDraft[],
  fields: DraftFields,
): { draft: CorrectionDraftInput | null; errors: Array<{ rowKeyId: string; error: string }> } {
  const errors: Array<{ rowKeyId: string; error: string }> = [];
  const lines: CorrectionLineInput[] = [];
  for (const d of drafts) {
    const result = parseManualLine(d);
    if (result.error !== undefined) errors.push({ rowKeyId: d.row.rowKeyId, error: result.error });
    else lines.push(result.input);
  }
  if (errors.length || lines.length === 0) return { draft: null, errors };
  return {
    draft: {
      title: fields.title.trim(),
      reason: fields.reason.trim(),
      kind: 'manual',
      lines,
      clientNote: normalizeClientNoteInput(fields.clientNote),
      settlement: { status: 'pending' },
    },
    errors,
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** One row the server named in an error's details (a label string, or a drifted-row object). */
const rowLabel = (row: unknown): string => {
  if (typeof row === 'string') return row;
  if (row && typeof row === 'object') {
    const r = row as { month?: string; channelName?: string; revenueSource?: string };
    return [r.month, r.channelName, r.revenueSource].filter(Boolean).join(' · ');
  }
  return '';
};

/** A readable message for an API error, with the first few rows the server named. */
export function correctionErrorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  const e = err as { message?: string; body?: { details?: { rows?: unknown } } } | null;
  const message = e?.message || fallback;
  const rows: unknown[] = Array.isArray(e?.body?.details?.rows) ? (e!.body!.details!.rows as unknown[]) : [];
  const named = rows.slice(0, 3).map(rowLabel).filter(Boolean);
  if (named.length === 0) return message;
  const more = rows.length > named.length ? `; +${rows.length - named.length} more` : '';
  return `${message} (${named.join('; ')}${more})`;
}
